import { FunctionComponent, useEffect, useState } from 'react'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Add } from '@mui/icons-material'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
    CREATE_RESERVATION_EVENT,
    DELETE_RESERVATION_EVENT,
    GET_RESERVATION_EVENT,
    UPDATE_RESERVATION_EVENT
} from '@/modules/API/daycare/eventDayHandler.ts'
import { AdultReservation, EventDay, ReservationChild, ReservationParent } from '@/types/EventDay.ts'
import Loading from '@/components/Loading/Loading.tsx'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { SelectOptionsDeprecated } from '@/types/Select.ts'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import { GET_PARENT } from '@/modules/API/parent/parentHandler.ts'
import { Parent } from '@/types/Parent.ts'
import styles from './parentReservationDialog.module.scss'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ParentReservationDialogProps {
    open: boolean
    handleClose: () => void
    event: EventDay
    daycareId: number
    setEventBooked: (value: boolean) => void
    isParent?: boolean
    refetchAllEvents: () => Promise<void>
}

const ParentReservationDialog: FunctionComponent<ParentReservationDialogProps> = ({
    open,
    handleClose,
    event,
    daycareId,
    setEventBooked,
    isParent = false,
    refetchAllEvents
}: ParentReservationDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isReserved, setIsReserved] = useState<boolean>(event.booked || false)

    const [selectedChilds, setSelectedChilds] = useState<Array<string>>([])
    const [adultRequired] = useState<boolean>(event.attendantMin || false)
    const [selectedAdults, setSelectedAdults] = useState<Array<AdultReservation>>([])

    const [parentComment, setParentComment] = useState<string>('')

    const [nbChildsAlreadyReserved, setNbChildsAlreadyReserved] = useState<number>(0)

    const [getReservation, { loading: loadingReservation, data: dataReservation }] = useLazyQuery(
        GET_RESERVATION_EVENT,
        {
            variables: {
                event_id: event.id
            }
        }
    )

    useEffect(() => {
        if (dataReservation) {
            if (dataReservation.getReservationEvent.reservationParent.length > 0) {
                setSelectedAdults(
                    dataReservation.getReservationEvent.reservationParent.map(
                        (parent: ReservationParent): AdultReservation => ({
                            name: parent.name,
                            selfReservation: parent.selfReservation
                        })
                    )
                )
            }
            if (dataReservation.getReservationEvent.reservationChild.length > 0) {
                setSelectedChilds(
                    dataReservation.getReservationEvent.reservationChild.map((reservation: ReservationChild) =>
                        Number(reservation.childId).toString()
                    )
                )
                setNbChildsAlreadyReserved(dataReservation.getReservationEvent.reservationChild.length)
            }
        }
    }, [dataReservation])

    useEffect(() => {
        if (isReserved) {
            getReservation()
        } else {
            setSelectedChilds([])
            setSelectedAdults([])
            setNbChildsAlreadyReserved(0)
        }
    }, [isReserved])

    const [parent, setParent] = useState<Parent | null>(null)

    const { loading: loadingParent, data: dataParent } = useQuery(GET_PARENT, {
        skip: !isParent
    })

    useEffect(() => {
        if (dataParent) {
            setParent(dataParent.getParent)
        }
    }, [dataParent])

    const [createReservation, { loading: loadingCreate }] = useMutation(CREATE_RESERVATION_EVENT, {
        variables: {
            event_id: event.id,
            childs_id: selectedChilds.map((child) => parseInt(child)),
            adults: selectedAdults.filter((adult) => adult.name !== ''),
            daycare_id: daycareId,
            comment: parentComment
        },
        onCompleted: () => {
            showNotificationSuccess(t('reservationCreated'))

            refetchAllEvents()

            setIsReserved(true)
            setEventBooked(true)

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorReservationCreated'))
        }
    })

    const [updateReservation, { loading: loadingUpdate }] = useMutation(UPDATE_RESERVATION_EVENT, {
        variables: {
            reservation_id: dataReservation?.getReservationEvent.id,
            event_id: event.id,
            childs_id: selectedChilds.map((child) => parseInt(child)),
            adults: selectedAdults.filter((adult) => adult.name !== ''),
            daycare_id: daycareId,
            comment: parentComment
        },
        onCompleted: () => {
            showNotificationSuccess(t('reservationUpdated'))

            refetchAllEvents()

            getReservation()
            setEventBooked(true)

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorReservationUpdated'))
        }
    })

    const [deleteReservation, { loading: loadingDelete }] = useMutation(DELETE_RESERVATION_EVENT, {
        variables: {
            daycare_id: daycareId,
            reservation_id: dataReservation?.getReservationEvent.id,
            comment: parentComment
        },
        onCompleted: () => {
            showNotificationSuccess(t('reservationDeleted'))

            refetchAllEvents()

            setIsReserved(false)
            setEventBooked(false)
            setSelectedAdults([])
            setSelectedChilds([])
            setNbChildsAlreadyReserved(0)

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorReservationDeleted'))
        }
    })

    function handleUpdateReservation(): void {
        if (
            selectedAdults.filter((adult: AdultReservation): boolean => adult.name !== '').length === 0 &&
            selectedChilds.length === 0
        ) {
            deleteReservation()
        } else {
            updateReservation()
        }
    }

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options:
                event?.childsMatching.map((child) => {
                    return {
                        name: child.childName.valueOf(),
                        value: Number(child.childId).toString()
                    }
                }) || []
        }
    ]

    function handleChangeAdultName(value: string, index: number): void {
        const newAdults: AdultReservation[] = [...selectedAdults]
        newAdults[index].name = value
        setSelectedAdults(newAdults)
    }

    function handleAddNewAdult(name: string): void {
        if (
            event.parentOnly ||
            (selectedAdults.length < event.attendant &&
                selectedAdults.length < selectedChilds.length * 2 &&
                event.attendant - event.attendantBooked > 0)
        ) {
            setSelectedAdults([...selectedAdults, { name: name, selfReservation: false }])
        }
    }

    useEffect(() => {
        if ((event.parentOnly || event.attendantMin) && selectedAdults.length === 0 && parent) {
            setSelectedAdults([{ name: parent.firstname, selfReservation: false }])
        }
    }, [parent, event])

    const disabledUpdateButton: boolean =
        (event.parentOnly
            ? true
            : selectedChilds.length === 0 ||
              JSON.stringify(selectedChilds) ===
                  JSON.stringify(
                      dataReservation?.getReservationEvent.reservationChild.map((child: ReservationChild) =>
                          child.childId.toString()
                      )
                  )) &&
        JSON.stringify(selectedAdults.filter((adult: AdultReservation): boolean => adult.name !== '')) ===
            JSON.stringify(
                dataReservation?.getReservationEvent.reservationParent.map((parent: AdultReservation) => ({
                    name: parent.name,
                    selfReservation: parent.selfReservation
                }))
            )

    const disabledConfirmButton: boolean =
        loadingCreate ||
        (event.parentOnly
            ? selectedAdults.filter((adult: AdultReservation): boolean => adult.name !== '').length === 0
            : adultRequired
              ? selectedChilds.length === 0 || selectedAdults.length === 0
              : selectedChilds.length === 0)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={event.booked ? t('editBook') : t('book')} parent />
            <DialogBody className={styles.DialogBody}>
                {(loadingReservation || loadingParent) && <Loading overall={false} />}
                {(loadingCreate || loadingUpdate || loadingDelete) && <Loading overall={true} />}
                <div className={styles.TextContainer}>
                    <p className={styles.Title}>{event.title}</p>
                    <p className={styles.Date}>
                        {format(parse(event.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        }) +
                            ' ' +
                            t('from').toLowerCase() +
                            ' ' +
                            format(parse(event.startHour, 'HH:mm:ss', new Date()), 'HH:mm') +
                            ' ' +
                            t('to').toLowerCase() +
                            ' ' +
                            format(parse(event.endHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                    </p>
                </div>
                <div className={styles.Container}>
                    {!event.parentOnly && event.places > 0 && (
                        <div className={styles.InputContainer}>
                            <SelectMultiple
                                label={t('myChilds')}
                                selectValues={selectedChilds}
                                setSelectValues={setSelectedChilds}
                                selectOptions={selectOptionsChild}
                                isParent
                                className={styles.UserInput}
                                maxSelected={event.places - (event.placesBooked - nbChildsAlreadyReserved)}
                            />
                        </div>
                    )}
                    {!event.parentOnly &&
                        adultRequired &&
                        selectedChilds.length > 0 &&
                        (selectedAdults.length === 0 || selectedAdults[0].name === '') && (
                            <div className={styles.InputContainer}>{t('oneAdultRequired')}</div>
                        )}
                    {(event.parentOnly ||
                        (selectedAdults.length < event.attendant &&
                            selectedAdults.length < selectedChilds.length * 2 &&
                            event.attendant - event.attendantBooked > 0) ||
                        selectedAdults.length > 0) && (
                        <div className={styles.InputContainer}>
                            {selectedAdults.length > 0 &&
                                selectedAdults.map((adult: AdultReservation, index: number) => (
                                    <div key={index} className={styles.AdultReservation}>
                                        <UserInput
                                            label={t('adultFirstname')}
                                            value={adult.name}
                                            setValue={(value: string) => handleChangeAdultName(value, index)}
                                            parent
                                            required={index === 0 && adultRequired}
                                            className={styles.UserInput}
                                        />
                                    </div>
                                ))}
                            {(event.parentOnly ||
                                (selectedAdults.length < event.attendant &&
                                    selectedAdults.length < selectedChilds.length * 2 &&
                                    event.attendant - event.attendantBooked > 0)) && (
                                <ButtonTextIcon
                                    type={'custom'}
                                    parent
                                    onClick={() =>
                                        handleAddNewAdult((selectedAdults.length === 0 && parent?.firstname) || '')
                                    }
                                    customIcon={<Add />}
                                    customText={t('addAdult')}
                                />
                            )}
                        </div>
                    )}
                    {((!isReserved && !disabledConfirmButton) || (isReserved && !disabledUpdateButton)) && (
                        <>
                            <div className={styles.BreakLine}></div>
                            <div className={styles.TextAreaInput}>
                                <p className={styles.TextAreaTitle}>{t('comment')}</p>
                                <TextArea
                                    placeholder={t('comment')}
                                    maxCount={200}
                                    className={styles.TextArea}
                                    value={parentComment}
                                    onChange={setParentComment}
                                    parent={true}
                                />
                            </div>
                        </>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} parent className={styles.Button} />
                        {isReserved ? (
                            <ButtonTextIcon
                                type={'update'}
                                onClick={handleUpdateReservation}
                                parent
                                className={styles.Button}
                                disabled={loadingUpdate || disabledUpdateButton}
                            />
                        ) : (
                            <ButtonTextIcon
                                type={'confirm'}
                                onClick={createReservation}
                                parent
                                className={styles.Button}
                                disabled={disabledConfirmButton}
                            />
                        )}
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ParentReservationDialog
